/**
 * Transform site content criteria, ensuring IAB category IDs are integers
 */
export const transformSiteContentCriteria = (criteria) => {
	if (!criteria) return {};

	const transformed = { ...criteria };

	// Convert IAB category IDs to integers (GARM and Sentiment IDs remain strings)
	if (transformed.categories?.length > 0) {
		transformed.categories = transformed.categories.map((cat) => ({
			...cat,
			id: parseInt(cat.id, 10),
		}));
	}

	// Convert internal type "default" to API type "taxonomy" with nested taxonomy type
	if (transformed.type === "default") {
		transformed.type = "taxonomy";
		if (transformed.taxonomy) {
			transformed.taxonomy = { type: "default", ...transformed.taxonomy };
		}
	}

	// Strip react-querybuilder IDs from custom criteria filterBy
	if (transformed.custom?.filterBy) {
		const filterBy = { ...transformed.custom.filterBy };
		delete filterBy.id;
		if (filterBy.rules) {
			filterBy.rules = filterBy.rules.map(({ id, ...rule }) => rule);
		}
		transformed.custom = { ...transformed.custom, filterBy };
	}

	return transformed;
};

/**
 * Transform site content modifier, ensuring count/lookback are integers
 */
export const transformSiteContentModifier = (modifier) => {
	if (!modifier) return {};

	const transformed = { ...modifier };

	// Ensure count is an integer, remove if null
	if (transformed.count != null) {
		transformed.count = parseInt(transformed.count, 10);
	} else {
		delete transformed.count;
	}

	// Ensure lookback is an integer, remove if null
	if (transformed.lookback != null) {
		transformed.lookback = parseInt(transformed.lookback, 10);
	} else {
		delete transformed.lookback;
	}

	// Only include timeUnit for timeSpent modifier type
	if (transformed.type !== "timeSpent") {
		delete transformed.timeUnit;
	}

	// Remove null filterBy from payload
	if (transformed.filterBy == null) {
		delete transformed.filterBy;
	}

	// Transform custom modifiers: "custom|eventName" -> type: "custom" with custom object
	if (transformed.type?.startsWith("custom|")) {
		const eventName = transformed.type.split("|")[1];
		transformed.type = "custom";
		// Strip react-querybuilder IDs from filterBy before sending to API
		let filterBy = transformed.filterBy;
		if (filterBy) {
			filterBy = { ...filterBy };
			delete filterBy.id;
			if (filterBy.rules) {
				filterBy.rules = filterBy.rules.map(({ id, ...rule }) => rule);
			}
		}
		transformed.custom = {
			type: eventName,
			filterBy,
		};
		delete transformed.filterBy;
	}

	return transformed;
};

/**
 * Transform a single criteria from internal format to API format
 */
export const transformCriteriaForApi = (criteria) => {
	const { type, data } = criteria;

	switch (type) {
		case "domains":
			return { domains: data.domains || [] };

		case "technographics":
			return {
				technographics: {
					os: data.os || {},
					browser: data.browser || {},
					deviceType: data.deviceType || {},
				},
			};

		case "demographics": {
			// Extract IDs as objects with integer id and title
			const demographics = {};
			const toObj = (item) => ({ id: parseInt(item.id, 10), title: item.title });
			if (data.age?.length > 0) demographics.age = data.age.map(toObj);
			if (data.gender?.length > 0) demographics.gender = data.gender.map(toObj);
			if (data.householdIncome?.length > 0)
				demographics.householdIncome = data.householdIncome.map(toObj);
			if (data.jobTitle?.length > 0) demographics.jobTitle = data.jobTitle.map(toObj);
			if (data.childrenInHousehold?.length > 0)
				demographics.childrenInHousehold = data.childrenInHousehold.map(toObj);
			if (data.lengthOfResidence?.length > 0)
				demographics.lengthOfResidence = data.lengthOfResidence.map(toObj);
			if (data.homeownerOrRenter?.length > 0)
				demographics.homeownerOrRenter = data.homeownerOrRenter.map(toObj);
			if (data.maritalStatus?.length > 0)
				demographics.maritalStatus = data.maritalStatus.map(toObj);
			if (data.educationLevel?.length > 0)
				demographics.educationLevel = data.educationLevel.map(toObj);
			return { demographics };
		}

		case "location": {
			const location = { action: data.action || "include" };
			if (data.countries?.length > 0)
				location.countries = data.countries.map((item) => ({
					id: item.id,
					country_name: item.country_name,
				}));
			if (data.regions?.length > 0)
				location.regions = data.regions.map((item) => ({
					id: item.id,
					region_name: item.region_name,
					country_code: item.country_code,
				}));
			if (data.metros?.length > 0)
				location.metros = data.metros.map((item) => ({
					id: parseInt(item.id, 10),
					metro_name: item.metro_name,
					country_code: item.country_code,
				}));
			if (data.cities?.length > 0)
				location.cities = data.cities.map((item) => ({
					id: item.id,
					city_name: item.city_name,
					region_name: item.region_name,
					country_code: item.country_code,
				}));
			if (data.postalCodes?.length > 0) location.postalCodes = data.postalCodes;
			return { location };
		}

		case "subCohorts":
			return {
				subCohorts: {
					cohorts: (data.cohorts || []).map((item) => ({
						unique_id: item.unique_id,
						name: item.name,
					})),
					action: data.action || "include",
				},
			};

		case "realtimeSiteContent":
			return {
				siteContent: {
					targetType: "contextual",
					criteria: transformSiteContentCriteria(data.criteria),
					action: data.action || "include",
				},
			};

		case "contentConsumption": {
			const transformedCriteria = transformSiteContentCriteria(data.criteria);
			const hasCriteria =
				transformedCriteria && Object.keys(transformedCriteria).length > 0;
			return {
				siteContent: {
					targetType: "behavioral",
					criteria: hasCriteria ? transformedCriteria : { type: "all" },
					modifier: transformSiteContentModifier(data.modifier),
					action: data.action || "include",
				},
			};
		}

		case "syndicated":
			return {
				syndicated: {
					segments: (data.segments || []).map((item) => ({
						id: item.id,
						title: item.title,
						...(item.provider && { provider: item.provider }),
					})),
				},
			};

		case "customUserData": {
			// Handle GAM integration - convert entity IDs to integers
			if (data.type === "gam" && data.gam) {
				const transformGamEntities = (entities) =>
					(entities || []).map((entity) => ({
						...entity,
						id: parseInt(entity.id, 10),
					}));

				const gam = {
					...data.gam,
					gamCompanies: transformGamEntities(data.gam.gamCompanies),
					gamOrders: transformGamEntities(data.gam.gamOrders),
					gamLineItems: transformGamEntities(data.gam.gamLineItems),
					gamCreatives: transformGamEntities(data.gam.gamCreatives),
				};
				// ASB-3720: optional threshold / lookback / video. Send ints, and never send a null
				// (the API stores whatever it is given; null video would be a third "state").
				["count", "lookback"].forEach((key) => {
					if (gam[key] == null || gam[key] === "") delete gam[key];
					else gam[key] = parseInt(gam[key], 10);
				});
				if (typeof gam.video !== "boolean") delete gam.video;

				return { customUserData: { ...data, gam } };
			}
			return { customUserData: data };
		}

		default:
			// Fallback: wrap data with type as key
			return { [type]: data };
	}
};

/**
 * Transform API criteria format to internal state format
 * Inverse of transformCriteriaForApi
 */
export const transformCriteriaFromApi = (apiCriteria) => {
	// apiCriteria is an object with a single key like { domains: [...] }
	const entries = Object.entries(apiCriteria);
	if (entries.length === 0) return { type: null, data: {} };

	const [apiType, data] = entries[0];

	switch (apiType) {
		case "domains":
			return { type: "domains", data: { domains: data || [] } };

		case "technographics":
			return { type: "technographics", data: data };

		case "demographics": {
			// Convert demographics items to objects { id, title }
			// Items may be objects { id, title } or plain integers
			const demographics = {};
			Object.entries(data || {}).forEach(([field, items]) => {
				if (Array.isArray(items) && items.length > 0) {
					demographics[field] = items.map((item) => {
						if (typeof item === "object") {
							return {
								id: String(item.id),
								title: item.title || String(item.id),
							};
						}
						return { id: String(item), title: String(item) };
					});
				}
			});
			return { type: "demographics", data: demographics };
		}

		case "location": {
			const location = { action: data.action || "include" };
			if (data.countries?.length) {
				location.countries = data.countries.map((item) => {
					return {
						id: item.id,
						country_name: item.country_name || item.id,
					};
				});
			}
			if (data.regions?.length) {
				location.regions = data.regions.map((item) => {
					return {
						id: item.id,
						region_name: item.region_name || item.id,
						country_code: item.country_code || "",
					};
				});
			}
			if (data.metros?.length) {
				location.metros = data.metros.map((item) => {
					return {
						id: String(item.id),
						metro_name: item.metro_name || String(item.id),
						country_code: item.country_code || "",
					};
				});
			}
			if (data.cities?.length) {
				location.cities = data.cities.map((item) => {
					return {
						id: item.id,
						city_name: item.city_name || item.id,
						region_name: item.region_name || "",
						country_code: item.country_code || "",
					};
				});
			}
			if (data.postalCodes?.length) {
				location.postalCodes = data.postalCodes;
			}
			return { type: "location", data: location };
		}

		case "subCohorts":
			return {
				type: "subCohorts",
				data: {
					// Cohort items may be objects { unique_id: "...", name: "..." } or just { unique_id: "..." }
					cohorts: (data.cohorts || []).map((item) => {
						return {
							unique_id: item.unique_id,
							name: item.name || item.unique_id,
						};
					}),
					action: data.action || "include",
				},
			};

		case "siteContent": {
			// Determine if contextual or behavioral based on targetType
			const isContextual = data.targetType === "contextual";

			// Transform criteria from API format to internal format
			// Convert API type "taxonomy" back to internal type using taxonomy.type (e.g. "default")
			let criteria = data.criteria || {};
			if (criteria.type === "taxonomy" && criteria.taxonomy?.type) {
				const taxonomyType = criteria.taxonomy.type;
				const { type: _discardType, ...taxonomyRest } = criteria.taxonomy;
				criteria = { ...criteria, type: taxonomyType, taxonomy: taxonomyRest };
			}

			// Transform modifier from API format to internal format
			let modifier = data.modifier || {};
			if (modifier.type === "custom" && modifier.custom) {
				// Convert API format { type: "custom", custom: { type: "eventName", filterBy } }
				// to internal format { type: "custom|eventName", filterBy }
				modifier = {
					...modifier,
					type: `custom|${modifier.custom.type}`,
					filterBy: modifier.custom.filterBy,
				};
				delete modifier.custom;
			}

			return {
				type: isContextual ? "realtimeSiteContent" : "contentConsumption",
				data: {
					criteria,
					modifier,
					action: data.action || "include",
				},
			};
		}

		case "syndicated":
			return {
				type: "syndicated",
				data: {
					// Segment items may be objects { id: "...", title: "...", provider: "..." } or just { id: "..." }
					segments: (data.segments || []).map((item) => ({
						id: item.id,
						title: item.title || item.id,
						...(item.provider && { provider: item.provider }),
					})),
				},
			};

		case "customUserData":
			return { type: "customUserData", data: data };

		default:
			return { type: null, data: {} };
	}
};
